import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import _ from 'underscore';

import ExternalFileUpload from 'external-files/ExternalFileUpload';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import * as NoteLinkedFileUtil from 'notebook/notes/attachments/NoteLinkedFileUtil';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {NoteLinkedGroupItemProps} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import NoteConstants, {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {FromServerNoteLinkedFile, NoteLinkedFile, NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY} from 'ui-library/Colors';
import DnDCanvas from 'ui-library/drag-and-drop/DnDCanvas';
import {DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import {DND_BLOCK_SPACING} from 'ui-library/drag-and-drop/DnDStyleConstants';
import {HoverPosition} from 'ui-library/drag-and-drop/hooks/useDnDCanvasDragState';
import moveSourceAdjacentToTarget from 'ui-library/drag-and-drop/move-item-handlers/moveSourceAdjacentToTarget';
import {ElementClipboardEvent} from 'util/Types';

import DraggableItem from './DraggableItem';
import {NotePartGrid as NotePartGridType} from './NotePartGridGroupConstants';
import {
  computeRowsForFlowLayout,
  DraggableFlowItem,
  MAX_ITEM_WIDTH,
  MIN_ITEM_WIDTH,
} from './util/computeRowsForFlowLayout';
import {GRID_IMAGE_ACCEPT, isGridInsertableImageType, MAX_GRID_IMAGES} from './util/imageTypeUtils';

const DEFAULT_CONTAINER_WIDTH = 700;
const DEFAULT_PLACEHOLDER_WIDTH = 300;
const {NOTE_LINKED_OBJECT} = NoteConstants;

export interface NotePartGridProps {
  notePartCid: string;
  group: NotePartGridType;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  itemProps?: NoteLinkedGroupItemProps;
}

/**
 * NotePartGrid renders a drag-and-drop grid of DraggableItems within a DnDCanvas.
 * Each item in the grid corresponds to a containedNotePart (e.g. an image attachment)
 * within a NoteLinkedGroup of kind NOTE_PART_GRID.
 */
export default function NotePartGrid({
  notePartCid,
  group,
  editor,
  editorProps,
  itemProps,
}: NotePartGridProps): JSX.Element {
  const [selectedIndexInGrid, setSelectedIndexInGrid] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const prevContainedNotePartsRef = useRef(group.containedNoteParts);

  // Use editor width for container width
  const containerWidth = (editor.state as {editorWidth?: number}).editorWidth || DEFAULT_CONTAINER_WIDTH;

  const isEmpty = group.containedNoteParts.length === 0;

  // Reset selectedIndexInGrid if it falls out of bounds (e.g. after undo/redo or failed upload removal)
  useEffect(() => {
    if (selectedIndexInGrid != null && selectedIndexInGrid >= group.containedNoteParts.length) {
      setSelectedIndexInGrid(
        group.containedNoteParts.length > 0 ? group.containedNoteParts.length - 1 : null
      );
    }
  }, [group.containedNoteParts.length, selectedIndexInGrid]);

  // If we delete an item in the notepartgrid, select the entire notepartgrid and unselect all photos within the grid.
  // If we add an item, select the added item if it's not a placeholder.
  useEffect(() => {
    const prev = prevContainedNotePartsRef.current;
    prevContainedNotePartsRef.current = group.containedNoteParts;

    if (prev.length < group.containedNoteParts.length) {
      // Item added — select it if it's a real item (not an upload placeholder)
      const prevCids = new Set(prev.map((p) => p.cid));
      const addedIndex = group.containedNoteParts.findIndex((p) => !prevCids.has(p.cid) && !p.placeholder);
      if (addedIndex !== -1) {
        setSelectedIndexInGrid(addedIndex);
      }
    } else if (prev.length > group.containedNoteParts.length) {
      // Item deleted. We need to distinguish between user-initiated deletions and
      // internal placeholder removals (e.g. after an upload failure).
      const currentCids = new Set(group.containedNoteParts.map((p) => p.cid));
      const removedItems = prev.filter((p) => !currentCids.has(p.cid));
      const removedOnlyPlaceholders = removedItems.length > 0 && removedItems.every((p) => p.placeholder);
      if (!removedOnlyPlaceholders) {
        // Real item deleted. Select the entire notepartgrid and unselect any specific photo inside the notepartgrid.
        const notePartGridIndex = editor.getIndex(notePartCid);
        if (notePartGridIndex !== -1) {
          const gridNotePart = editor.props.note.noteParts[notePartGridIndex];
          const selection = editor.selectionAtItem(gridNotePart);
          if (selection != null) {
            editorProps.requestSelection(selection);
            setSelectedIndexInGrid(null);
          }
        }
      }
    }
  }, [group.containedNoteParts, editor, editorProps, notePartCid]);

  // Clear internal selection when clicking outside the grid container.
  // GridItemWrapper.handleMouseDown stopPropagation() prevents the mediocre editor from knowing about
  // clicks within the grid, so we mirror the editor's own pattern
  // (MultiSelectListUtil.multiWindowMouseDownHandler) with a document-level listener
  // that deselects when the user clicks anywhere outside.
  useEffect(() => {
    if (selectedIndexInGrid == null) {
      return;
    }

    const handleDocumentMouseDown = (e: MouseEvent): void => {
      if (gridContainerRef.current && !gridContainerRef.current.contains(e.target as Node)) {
        // Don't clear when clicking into portaled menus/dropdowns — matches the mediocre
        // editor's exclusion list so that interactions with context menus, image editor
        // popovers, etc. don't inadvertently deselect the grid item.
        const target = e.target;
        if (!(target instanceof Element)) {
          return;
        }
        if (target.closest('.dropdown-menu, .backdrop, .menu, .js-mediocre-active-edit, .attachDropdown')) {
          return;
        }
        setSelectedIndexInGrid(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [selectedIndexInGrid]);

  // Compute the 2D row layout from the flat containedNoteParts list.
  // This produces the Array<Array<DraggableFlowItem>> that DnDCanvas expects.
  // Only compute when we have items (computeRowsForFlowLayout asserts non-empty).
  //
  // Note: We need to account for the grid container having a 12px padding on each side and the 12px gap
  // between items in each row.
  const rows = useMemo(
    () =>
      isEmpty
        ? [[]]
        : computeRowsForFlowLayout(
            group.containedNoteParts,
            containerWidth - DND_BLOCK_SPACING * 2,
            MIN_ITEM_WIDTH,
            MAX_ITEM_WIDTH,
            0,
            12
          ),
    [group.containedNoteParts, containerWidth, isEmpty]
  );

  // Determine maxColumnCount from the computed layout (max items in any single row).
  const maxColumnCount = useMemo(() => Math.max(1, ...rows.map((row) => row.length)), [rows]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Persist an updated group back to the editor via itemUpdateHandler.
   */
  const updateGroup = useCallback(
    (updatedGroup: NotePartGridType): void => {
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex !== -1) {
        editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
      }
    },
    [editor, notePartCid]
  );

  // ── Upload ──────────────────────────────────────────────────────────────────

  const handleUploadClick = useCallback((): void => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Replace a placeholder note part with the actual uploaded NoteLinkedFile.
   */
  const replacePlaceholder = useCallback(
    (taskId: string, serverNoteLinkedFile: FromServerNoteLinkedFile): void => {
      // The note may have been locked while the upload was in flight. Silently discard; placeholder will be
      // cleaned up when the note reloads from the server after the lock resolves.
      if (editor.props.locked) {
        return;
      }

      // Generate a CID for the NoteLinkedFile and register it
      const noteLinkedFileCid = _.uniqueId('note-linked-object-');
      const cidIdMap: {[cid: string]: string | number} = {[noteLinkedFileCid]: serverNoteLinkedFile.id};
      editor.props.registerObjects(cidIdMap);

      const noteLinkedFileWithCid = {
        ...serverNoteLinkedFile,
        cid: noteLinkedFileCid,
      };

      // Create a proper note part for the uploaded file
      const notePartCidForFile = _.uniqueId('note-part-');
      const noteLinkedFilePart: NotePart = {
        ...makeDefaultItem(NOTE_LINKED_OBJECT),
        item_id: serverNoteLinkedFile.id,
        alignment: 'left',
        placeholder: false,
        cid: notePartCidForFile,
        item: noteLinkedFileWithCid as unknown as NotePart['item'],
      };

      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const newContainedNoteParts = currentGroup.containedNoteParts.map((part: NotePart) =>
        part.taskId === taskId
          ? {...noteLinkedFilePart, itemWidth: part.itemWidth ?? DEFAULT_PLACEHOLDER_WIDTH}
          : part
      );

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
    },
    [editor, notePartCid]
  );

  /**
   * Remove a placeholder note part on upload failure because an empty placeholder is not useful in the grid.
   */
  const removePlaceholder = useCallback(
    (taskId: string): void => {
      if (editor.props.locked) {
        return;
      }
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const newContainedNoteParts = currentGroup.containedNoteParts.filter(
        (part: NotePart) => part.taskId !== taskId
      );

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
    },
    [editor, notePartCid]
  );

  const uploadFiles = useCallback(
    (files: Array<File>): void => {
      const fileId = editor.props.fileId;
      const taskIdAndDeferreds = ExternalFileUpload.createTaskAndUploadFiles(fileId, files);

      // We construct placeholders manually rather than using makeFilePlaceholderItem because
      // the latter does not include the itemWidth property, which is required for the grid.
      const placeholderNoteParts: Array<NotePart> = taskIdAndDeferreds.map(({taskId}: {taskId: string}) => {
        const placeholder = makeDefaultItem(NOTE_LINKED_OBJECT);
        return {
          ...placeholder,
          alignment: 'left' as const,
          placeholder: true,
          taskId,
          cid: `temp-${taskId}`,
          item: null,
          item_id: null,
          itemWidth: DEFAULT_PLACEHOLDER_WIDTH,
        };
      });

      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: [...currentGroup.containedNoteParts, ...placeholderNoteParts],
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});

      // TODO(BNCH-145487): Replace _.defer with redux/store-based state management to avoid race
      // conditions when multiple files are uploaded in quick succession (same issue as NoteLinkedFile).
      _.defer(() => {
        taskIdAndDeferreds.forEach(
          (
            {
              taskId,
              fileUploadDeferred,
            }: {taskId: string; fileUploadDeferred: JQueryPromise<FromServerNoteLinkedFile>},
            index: number
          ) => {
            fileUploadDeferred
              .done((res: FromServerNoteLinkedFile | undefined) => {
                if (res != null) {
                  replacePlaceholder(taskId, res);
                } else {
                  removePlaceholder(taskId);
                  notifications.error(`Failed to upload image: ${files[index].name}`);
                }
              })
              .fail(() => {
                removePlaceholder(taskId);
                notifications.error(`Failed to upload image: ${files[index].name}`);
              });
          }
        );
      });
    },
    [editor, notePartCid, replacePlaceholder, removePlaceholder]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const allFiles = Array.from(e.target.files || []);
      if (allFiles.length === 0) {
        return;
      }

      // Runtime filter: the accept attribute is advisory and can be bypassed,
      // so we also reject non-previewable image types (webp, heic, heif, etc.) here.
      const files = allFiles.filter((f) => isGridInsertableImageType(f.type));
      const rejectedCount = allFiles.length - files.length;
      if (rejectedCount > 0) {
        // TODO(BNCH-147263): Add webp to the allowlist once preview generation support is implemented.
        notifications.warn(
          'Some image types (e.g. WebP, HEIC) are not supported in the image grid and were not added.'
        );
      }
      if (files.length === 0) {
        e.target.value = '';
        return;
      }

      const currentImageCount = group.containedNoteParts.length;
      const availableSlots = MAX_GRID_IMAGES - currentImageCount;
      if (availableSlots <= 0) {
        notifications.warn(`Maximum of ${MAX_GRID_IMAGES} images allowed side-by-side.`);
        e.target.value = '';
        return;
      }
      if (files.length > availableSlots) {
        notifications.warn(
          `You can only add ${availableSlots} more image${availableSlots !== 1 ? 's' : ''}. Maximum of ${MAX_GRID_IMAGES} images allowed.`
        );
        files.splice(availableSlots);
      }

      uploadFiles(files);
      e.target.value = '';
    },
    [group.containedNoteParts.length, uploadFiles]
  );

  // ── DnDCanvas: onMoveItem ─────────────────────────────────────────────────

  const handleMoveItem = useCallback(
    (source: DraggableFlowItem, target: DraggableFlowItem, hoverPosition?: HoverPosition): void => {
      const {newLayout, layoutChanged} = moveSourceAdjacentToTarget(rows, source, target, hoverPosition);
      if (!layoutChanged) {
        return;
      }
      // Flatten the reordered 2D layout back into a 1D containedNoteParts array
      const reorderedNoteParts = newLayout.flat().map((item) => item.notePart);

      // Adjust selected index in grid if the currently selected item has shifted to a new position.
      if (selectedIndexInGrid != null) {
        const selectedCid = group.containedNoteParts[selectedIndexInGrid].cid;
        if (selectedCid) {
          const newIndex = reorderedNoteParts.findIndex((part) => part.cid === selectedCid);
          setSelectedIndexInGrid(newIndex >= 0 ? newIndex : null);
        }
      }

      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: reorderedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [rows, group, updateGroup, selectedIndexInGrid]
  );

  // ── DraggableItem callbacks ────────────────────────────────────────────────

  const handleSelectInGrid = useCallback((index: number): void => {
    setSelectedIndexInGrid(index >= 0 ? index : null);
  }, []);

  // TODO(BNCH-144428): Implement keyboard navigation between DraggableItems in a future iteration.
  const handleMoveSelectionInGrid = useCallback((index: number, direction: 'prev' | 'next'): void => {}, []);

  const handleRemoveItem = useCallback(
    (index: number): void => {
      const newContainedNoteParts = [...group.containedNoteParts];
      newContainedNoteParts.splice(index, 1);

      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);

      // Adjust selection if needed
      if (selectedIndexInGrid != null) {
        if (index === selectedIndexInGrid) {
          setSelectedIndexInGrid(null);
        } else if (index < selectedIndexInGrid) {
          setSelectedIndexInGrid(selectedIndexInGrid - 1);
        }
      }
    },
    [group, updateGroup, selectedIndexInGrid]
  );

  const handleFinalizeItemWidth = useCallback(
    (index: number, width: number): void => {
      const newContainedNoteParts = [...group.containedNoteParts];
      newContainedNoteParts[index] = {
        ...newContainedNoteParts[index],
        itemWidth: width,
      };
      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [group, updateGroup]
  );

  const handleUpdateNoteLinkedObject = useCallback(
    (index: number, updates: Partial<NoteLinkedFile>): void => {
      const notePart = group.containedNoteParts[index];
      const newContainedNoteParts = [...group.containedNoteParts];
      newContainedNoteParts[index] = {
        ...notePart,
        item: {...(notePart.item as NoteLinkedFile), ...updates},
      };
      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [group, updateGroup]
  );

  // Use the same CID registration flow as top-level NoteLinkedFiles so that
  // the save payload includes the updated tfiFileMeta and the item_id stays current.
  const handleSetUpdatedNoteLinkedFile = useCallback(
    (index: number, noteLinkedFile: FromServerNoteLinkedFile): void => {
      const currentNotePart = group.containedNoteParts[index];
      const cidIdMap: CidIdMap = {};
      const updatedNotePart = NoteLinkedFileUtil.maybeReplaceExternalFile(
        currentNotePart,
        currentNotePart,
        noteLinkedFile,
        null,
        cidIdMap
      );
      editor.props.registerObjects(cidIdMap);

      const newServerId = Object.values(cidIdMap)[0] ?? updatedNotePart.item_id;
      const newContainedNoteParts = [...group.containedNoteParts];
      newContainedNoteParts[index] = {...updatedNotePart, item_id: newServerId};
      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [editor, group, updateGroup]
  );

  const handleSetClipboard = useCallback((_e: ElementClipboardEvent): void => {
    // TODO(BNCH-144143): Clipboard support will be added in a future iteration
  }, []);

  // Clicking empty space in the grid container deselects the current item.
  // GridItemWrapper.handleMouseDown calls stopPropagation(), so any click that
  // reaches this handler is guaranteed to be outside an item's content area.
  // Note: We use mouseDown instead of click here because of the complex interactions between the grid,
  // editor, and DnDCanvas which can, for example, prevent clicks from "completing" on an individual item in
  // the grid, and instead "starting" on a drag handle, but "finishing" on the grid container.
  const handleContainerMouseDown = useCallback((): void => {
    setSelectedIndexInGrid(null);
  }, []);

  // ── DnDCanvas: renderItem ─────────────────────────────────────────────────
  // DnDBlock memoizes the rendered element via useMemo keyed on the
  // renderItem reference. The callback identity changes when selection
  // state changes (selectedIndexInGrid) or when CID→ID resolution
  // updates after a save (itemProps?.resolvedContainedItemIds).

  const renderItem = useCallback(
    (item: DraggableFlowItem, dragHandleProps: DnDDragHandleProps, _index: number): JSX.Element => (
      <DraggableItem
        item={item}
        dragHandleProps={dragHandleProps}
        editor={editor}
        editorProps={editorProps}
        isSelectedInGrid={selectedIndexInGrid === item.index}
        onRemoveItem={handleRemoveItem}
        onFinalizeItemWidth={handleFinalizeItemWidth}
        onSelectInGrid={handleSelectInGrid}
        onMoveSelectionInGrid={handleMoveSelectionInGrid}
        onSetClipboard={handleSetClipboard}
        requestSelection={editorProps.requestSelection}
        isLockedWithEditWarn={editor.isLockedWithEditWarn}
        onUpdateNoteLinkedObject={handleUpdateNoteLinkedObject}
        onSetUpdatedNoteLinkedFile={handleSetUpdatedNoteLinkedFile}
      />
    ),
    // resolvedContainedItemIds is not read in the callback but is included to bust DnDBlock's useMemo
    // when CID→ID mappings change after a save (editorProps reference can remain stable across saves).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editor,
      editorProps,
      selectedIndexInGrid,
      handleRemoveItem,
      handleFinalizeItemWidth,
      handleSelectInGrid,
      handleMoveSelectionInGrid,
      handleSetClipboard,
      handleUpdateNoteLinkedObject,
      handleSetUpdatedNoteLinkedFile,
      itemProps?.resolvedContainedItemIds,
    ]
  );

  // ── DnDCanvas: row containers ──────────────────────────────────────────────

  const renderDndRowContainer = useCallback(
    ({children}: {children: JSX.Element}, rowIndex: number): JSX.Element => (
      <div key={`dnd-row-${rowIndex}`} className={css(styles.dndRow)}>
        {children}
      </div>
    ),
    []
  );

  const itemContainers = useMemo(
    () =>
      rows.map(
        (_: unknown, i: number) => (props: {children: JSX.Element}) => renderDndRowContainer(props, i)
      ),
    [rows, renderDndRowContainer]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SelectableNotePart indentation={0}>
      <div ref={gridContainerRef} className={css(styles.gridContainer)}>
        <input
          ref={fileInputRef}
          type="file"
          multiple={true}
          accept={GRID_IMAGE_ACCEPT}
          aria-label="Upload images"
          onChange={handleFileChange}
          className={css(styles.hiddenFileInput)}
          disabled={editorProps.locked}
        />
        {isEmpty ? (
          <div className={css(styles.emptyState)}>
            <div className={css(styles.emptyStateContent)}>
              <div className={css(styles.emptyStateText)}>Add images to view them side-by-side</div>
              {!editorProps.locked && (
                <Button
                  onClick={handleUploadClick}
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  text="Upload Images"
                />
              )}
            </div>
          </div>
        ) : (
          <div onMouseDown={handleContainerMouseDown}>
            <DnDCanvas
              items={rows}
              maxColumnCount={maxColumnCount}
              minBlockWidth={MIN_ITEM_WIDTH}
              fitContent={true}
              onMoveItem={handleMoveItem}
              renderItem={renderItem}
              itemContainers={itemContainers}
            />
          </div>
        )}
      </div>
    </SelectableNotePart>
  );
}

const styles = StyleSheet.create({
  gridContainer: {
    position: 'relative',
    width: '100%',
  },
  hiddenFileInput: {
    display: 'none',
  },
  emptyState: {
    alignItems: 'center',
    border: `2px dashed ${GRAY}`,
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '200px',
    padding: '32px',
  },
  emptyStateContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    textAlign: 'center' as const,
  },
  emptyStateText: {
    color: GRAY,
    fontSize: '14px',
  },
  dndRow: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
  },
});
